import { useTranslation } from '@frontend/i18n';
import { ReviewsLandingTemplate } from '@frontend/reviews-landing-template';
import { theme } from '@frontend/theme';
import { MessageTemplate, Stepper, TagType, Text, useTooltip } from '@frontend/design-system';
import { overrideTextStyles, stepValueContainerStyles } from './styles';

type TemplateMessageProps = {
  templateString: string;
  review: () => void;
  previous: () => void;
  handleMessageChange: (message: string) => void;
  disableReview: boolean;
  initialTemplate: string;
  mappedTags: TagType[];
  helperTexts: { dynamicTagMessage?: string; requiredTagMessage?: string };
  isFormModeEdit: boolean;
  isTemplateTypeReview: boolean;
  saveToTemplateFormStore: () => void;
  templateLocations?: string[];
};

export const TemplateMessageStep = ({
  review,
  previous,
  handleMessageChange,
  isTemplateTypeReview,
  disableReview,
  initialTemplate,
  mappedTags,
  isFormModeEdit,
  helperTexts,
  saveToTemplateFormStore,
  templateLocations,
}: Omit<TemplateMessageProps, 'templateString'>) => {
  const { t } = useTranslation('templates-settings');

  const { Tooltip, tooltipProps, triggerProps } = useTooltip({
    placement: 'top',
  });

  return (
    <>
      <Stepper.Title css={{ marginBottom: theme.spacing(1.75) }}>{t('Template Message')}</Stepper.Title>
      <Stepper.Content>
        <ReviewsLandingTemplate
          handleMessageChange={handleMessageChange}
          helperTexts={helperTexts}
          initialTemplate={initialTemplate}
          isFormModeEdit={isFormModeEdit}
          isTemplateTypeReview={isTemplateTypeReview}
          mappedTags={mappedTags}
          saveToTemplateFormStore={saveToTemplateFormStore}
          templateLocations={templateLocations}
          variant='manual-template'
        />
      </Stepper.Content>

      {!isFormModeEdit && (
        <>
          <Stepper.ButtonBarAlternate>
            <Stepper.ButtonAlternate onClick={previous} position='secondary' type='none'>
              {t('Previous')}
            </Stepper.ButtonAlternate>
            <Stepper.ButtonAlternate
              onClick={() => {
                if (!disableReview) review();
              }}
              disabled={disableReview}
              position='primary'
              type='none'
            >
              <span {...(disableReview ? triggerProps : {})}> {t('Review')} </span>
            </Stepper.ButtonAlternate>
          </Stepper.ButtonBarAlternate>
          <Tooltip {...tooltipProps} css={{ textAlign: 'center' }}>
            {t('Add required dynamic fields in template message to review.')}
          </Tooltip>
        </>
      )}
    </>
  );
};

export const TemplateMessageStepCollapsed = ({
  initialTemplate,
  mappedTags,
  templateString,
}: Pick<TemplateMessageProps, 'initialTemplate' | 'templateString' | 'mappedTags'>) => {
  const { t } = useTranslation('templates-settings');
  return (
    <div css={{ ...stepValueContainerStyles, flexDirection: 'column' }}>
      <Text>{t('Template Message')}</Text>
      {templateString && (
        <MessageTemplate initialTemplate={initialTemplate} tags={mappedTags || []} isNewTag hasDynamicTags>
          <MessageTemplate.Preview css={{ ...overrideTextStyles, padding: 0, textTransform: 'none' }} />
        </MessageTemplate>
      )}
    </div>
  );
};
